import styled from 'styled-components';

import { BigSubtitle, SmallParagraph, style } from '@genially/design-system';

export const COLLAPSED_WIDTH_UNITS = 50;

interface PushSpacerProps {
  $isOpen: boolean;
}

/**
 * The panel itself is absolutely positioned so full screen can take the whole stage. This
 * spacer is a plain flex sibling of the genially inside ContentWrapper, so opening the
 * collapsed panel shrinks the genially instead of covering it and the host can keep
 * interacting with the content while the results are in view. Same trick as InviteSidebar.
 *
 * Below sm there is no room to push, so it stays at zero and the panel overlays as before.
 */
export const PushSpacer = styled.div<PushSpacerProps>(
  ({ theme: { size, transition }, $isOpen }) => ({
    height: '100%',
    width: $isOpen ? size.units(COLLAPSED_WIDTH_UNITS) : 0,
    flexShrink: 0,
    transition: transition.standard('width'),

    ...style.mixins.responsive.sm.downward({
      width: 0,
    }),
  }),
);

export const Header = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: size.units(1),
  paddingBottom: size.units(2),
}));

export const ExpandedHeader = styled.div(({ theme: { size } }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: size.units(2),
  paddingBottom: size.units(3),
}));

/** Out of the flow so the titles centre on the stage, not on what the button leaves. */
export const CollapseButton = styled.div({
  position: 'absolute',
  top: 0,
  right: 0,
});

export const HeaderTitles = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: size.half,
  overflow: 'hidden',
}));

export const StyledBigSubtitle = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  textAlign: 'center',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ExpandedTitle = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const ExpandedSubtitle = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(0.7),
}));

export const ScrollArea = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.size.units(2),
  flex: 1,
  overflowY: 'auto',

  padding: `${theme.size.units(2)} 0`,

  ...style.mixins.scrollbar(theme),
}));
